/**
 * People at scale, for mocks and stories only (no product code imports this): a split field with
 * 192 values and one distribution with 220 recipients from the data model, a file and typed rows,
 * on email and Slack, with rows to fix, values nobody receives and a few brand logos.
 * Names are generated and neutral. Pure functions: this file imports types only, so
 * `distributions.mockData.ts` can append to `MOCK_FIELDS` from it without a cycle.
 */
import type { RecipientBrandApi, RecipientInputApi, RecipientSourceInfoApi, ModelRecipientSourceApi } from "./distributions.types";

/** The scale distribution's id: look it up by this, never by name. Seeded in the default mock story. */
export const MOCK_SCALE_DISTRIBUTION_ID = "dist-people-at-scale";
export const MOCK_SCALE_DISTRIBUTION_NAME = "Territory manager pack";

/** A small draft with every row clean (nothing to fix), so Send now is enabled: for stories of the send confirmation. */
export const MOCK_READY_DISTRIBUTION_ID = "dist-ready-to-send";
export const MOCK_READY_DISTRIBUTION_NAME = "Ready to send pack";
/** Rows on the ready draft: the first rows of the scale list, all before its first row to fix. */
export const MOCK_READY_RECIPIENT_COUNT = 10;

export const MOCK_SCALE_FIELD_LABEL = "Territory";
export const MOCK_SCALE_VALUE_COUNT = 192;
/** Total recipients on the scale distribution. */
export const MOCK_SCALE_RECIPIENT_COUNT = 220;
/** Recipients per source kind. */
export const MOCK_SCALE_SOURCE_COUNTS = { model: 100, csv: 80, manual: 40 } as const;
/** Rows that need a fix: bad email (4), unknown value (3), missing address (2). */
export const MOCK_SCALE_FIX_COUNT = 9;
/** Rows that go to a Slack channel. */
export const MOCK_SCALE_SLACK_COUNT = 30;
/** Rows carrying their own `brand.logo_url`. */
export const MOCK_SCALE_LOGO_COUNT = 5;

export const mockScaleValues = (): string[] =>
  Array.from({ length: MOCK_SCALE_VALUE_COUNT }, (_, i) => `Territory ${String(i + 1).padStart(3, "0")}`);

/** Every 24th value (8 in all) has nobody receiving it. */
export const mockScaleHasNoOne = (index: number): boolean => index % 24 === 23;

const FIRST = ["Aisha", "Ben", "Carmen", "Dev", "Elif", "Felix", "Grace", "Hiro", "Ines", "Jonah", "Kira", "Luis", "Mei", "Nate", "Olga", "Pavel", "Rosa", "Sanjay", "Tess", "Umar", "Vera", "Wes", "Yusuf", "Zoe"];
const LAST = ["Patel", "Romero", "Okafor", "Lindgren", "Nakamura", "Byrne", "Haddad", "Costa", "Mueller", "Owusu", "Silva", "Tanaka", "Whitfield", "Kowalski", "Mensah", "Duarte", "Ahmed"];
const ACCENTS = ["#6D4AFF", "#0F7B6C", "#B54708", "#2B5BA8", "#9E2A6B"];

const logoUrl = (fill: string): string =>
  `data:image/svg+xml;utf8,${encodeURIComponent(
    `<svg xmlns="http://www.w3.org/2000/svg" width="240" height="60"><rect x="2" y="5" width="50" height="50" rx="12" fill="${fill}"/><rect x="64" y="22" width="150" height="16" rx="8" fill="${fill}"/></svg>`,
  )}`;

const personName = (i: number): string => `${FIRST[i % FIRST.length]} ${LAST[i % LAST.length]}`;
const slug = (i: number): string => `${personName(i).toLowerCase().replace(" ", ".")}${i + 1}`;
const valueNumber = (value: string): string => value.replace(/\D/g, "");

/**
 * The 220 rows, in position order. `values` is the split field's values (192), so rows only ever
 * name values that exist, except the three deliberate "not in data" rows.
 */
export const buildMockScaleRows = (values: readonly string[]): RecipientInputApi[] => {
  const covered = values.filter((_, i) => !mockScaleHasNoOne(i));
  const rows: RecipientInputApi[] = [];
  let slackCount = 0;
  const { model, csv } = MOCK_SCALE_SOURCE_COUNTS;
  for (let i = 0; i < MOCK_SCALE_RECIPIENT_COUNT; i++) {
    // The first pass covers every covered value once; the rest give some values a second person.
    const value = i < covered.length ? covered[i] : covered[(i - covered.length) * 5 % covered.length];
    const slack = i % 7 === 3 && slackCount < MOCK_SCALE_SLACK_COUNT;
    if (slack) slackCount += 1;
    const source = i < model ? "model" : i < model + csv ? "csv" : "manual";
    rows.push({
      position: i + 1,
      value,
      name: personName(i),
      channel: slack ? "slack" : "email",
      address: slack ? `#territory-${valueNumber(value)}-${i + 1}` : `${slug(i)}@example.com`,
      cc: [],
      formats: i % 3 === 0 ? ["pdf", "pptx"] : ["pdf"],
      template_id: null,
      theme_id: null,
      brand: null,
      extra: { team: `Team ${String.fromCharCode(65 + (i % 6))}` },
      source,
    });
  }
  // Rows to fix, spread through the list (positions 12, 52, 93, 134 / 191, 201, 211 / 40, 160).
  [11, 51, 92, 133].forEach((i) => Object.assign(rows[i], { channel: "email", address: `${slug(i)}@example` }));
  [190, 200, 210].forEach((i) => Object.assign(rows[i], { value: `Territory ${900 + i}` }));
  [39, 159].forEach((i) => Object.assign(rows[i], { channel: "email", address: "" }));
  // A few rows brand their own copy with a logo.
  [4, 36, 68, 100, 132].forEach((i, n) => {
    const brand: RecipientBrandApi = { name: `Partner ${String.fromCharCode(65 + n)}`, logo_url: logoUrl(ACCENTS[n]), accent: ACCENTS[n], mark: String.fromCharCode(65 + n) };
    rows[i].brand = brand;
  });
  return rows;
};

/** Where the rows came from: a file, plus the data model for the first 100. */
export const mockScaleModelSource = (modelReleaseId: string): ModelRecipientSourceApi => ({
  model_release_id: modelReleaseId,
  columns: [
    { target: "value", field: { kind: "dimension_attribute", model_field_id: "fld_territory" } },
    { target: "name", field: { kind: "dimension_attribute", model_field_id: "fld_manager_name" } },
    { target: "address", field: { kind: "dimension_attribute", model_field_id: "fld_manager_email" } },
  ],
  where: [],
  channel: "email",
  refresh_before_send: false,
});

export const mockScaleSource = (modelReleaseId: string): RecipientSourceInfoApi => ({
  kind: "list",
  file_name: "territory-managers.csv",
  uploaded_at: "2026-10-12T17:00:00Z",
  matched_columns: { territory: "value", full_name: "name", channel: "channel", address: "address", team: "extra.team" },
  model: mockScaleModelSource(modelReleaseId),
});
